import { useMemo, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/useEntity';
import { fmt } from '../../lib/formatters';
import { Spinner } from '../../components/ui/Spinner';
import { Badge } from '../../components/ui/Badge';
import { DataTable, type Column, type DataTableFilter } from '../../components/DataTable';
import { resumir, type MovimientoComision, type OrigenFecha } from '../../../shared/comisiones';

/**
 * Panel de comisiones PM (CEO, 22/10/2026). Comisión de los pedidos de
 * cliente CONFIRMADOS: devengada en la fecha de entrega (la que se da al
 * marcar el pedido ENTREGADO; en el histórico, la de cada entrega) y estimada
 * para lo pendiente en la ETA o, si no hay, en la fecha solicitada por el
 * cliente. Reglas y tests en shared/comisiones.ts.
 *
 * Datos: GET /api/comisiones (todos los movimientos); filtrar, agrupar y
 * pintar se hace aquí.
 */

type Movimiento = MovimientoComision & {
  pedido_estado: string;
  eta: string | null;
  fecha_solicitada: string | null;
  cliente_codigo: string;
  numero_cliente: string | null;
  numero_approx: string | null;
  fecha_pedido: string;
  producto_id: string | null;
  sku: string | null;
  producto_nombre: string | null;
  marca_nombre: string | null;
  gama_nombre: string | null;
  categoria_nombre: string | null;
  subcategoria_nombre: string | null;
  condicion: string | null;
};

type Desglose = 'mes' | 'cliente' | 'marca' | 'gama' | 'categoria' | 'subcategoria' | 'pedido';

const DESGLOSES: { key: Desglose; label: string }[] = [
  { key: 'mes', label: 'Mes' },
  { key: 'cliente', label: 'Cliente' },
  { key: 'marca', label: 'Marca' },
  { key: 'gama', label: 'Gama' },
  { key: 'categoria', label: 'Categoría' },
  { key: 'subcategoria', label: 'Subcategoría' },
  { key: 'pedido', label: 'Pedido' },
];

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

const ORIGEN_LABEL: Record<OrigenFecha, string> = {
  entregado: 'Marcado entregado',
  entrega: 'Entrega registrada',
  entrega_real: 'Entrega real de la línea',
  eta: 'ETA',
  solicitada: 'Fecha solicitada',
  sin_fecha: 'Sin fecha',
};

const numeroPedido = (m: Movimiento) => m.numero_cliente ?? m.numero_approx ?? m.fecha_pedido;
const pedidoHref = (m: Movimiento) => `/${m.cliente_codigo === 'TICNOVA' ? 'ticnova' : 'game'}/pedidos/${m.pedido_id}`;

function claveDe(m: Movimiento, por: Desglose): string {
  switch (por) {
    case 'mes': return m.fecha ? m.fecha.slice(0, 7) : 'sin fecha';
    case 'cliente': return m.cliente_codigo;
    case 'marca': return m.marca_nombre ?? 'Sin marca';
    case 'gama': return m.gama_nombre ?? 'Sin gama';
    case 'categoria': return [m.gama_nombre, m.categoria_nombre ?? 'Sin categoría'].filter(Boolean).join(' › ');
    case 'subcategoria': return [m.categoria_nombre, m.subcategoria_nombre ?? 'Sin subcategoría'].filter(Boolean).join(' › ');
    case 'pedido': return `${m.cliente_codigo} · ${numeroPedido(m)}`;
  }
}

function etiquetaMes(ym: string): string {
  if (!/^\d{4}-\d{2}$/.test(ym)) return ym;
  return `${MESES[Number(ym.slice(5, 7)) - 1]} ${ym.slice(0, 4)}`;
}

const media = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null);
const diasTexto = (d: number | null) => (d == null ? '—' : d === 0 ? 'en plazo' : d > 0 ? `+${fmt.int(d)} d` : `${fmt.int(d)} d`);

export function ComisionesPanel() {
  const [sp, setSp] = useSearchParams();
  const q = useQuery({
    queryKey: ['comisiones'],
    queryFn: () => api.get<{ movimientos: Movimiento[]; hoy: string }>('/comisiones'),
    staleTime: 60_000,
  });
  const hoy = q.data?.hoy ?? new Date().toISOString().slice(0, 10);
  const todos = useMemo(() => q.data?.movimientos ?? [], [q.data]);

  const set = (k: string, v: string) => {
    const next = new URLSearchParams(sp);
    if (v) next.set(k, v); else next.delete(k);
    if (k !== 'grupo') next.delete('grupo');
    setSp(next, { replace: true });
  };

  const clientes = useMemo(() => [...new Set(todos.map((m) => m.cliente_codigo))].sort(), [todos]);
  const anios = useMemo(() => {
    const s = new Set(todos.filter((m) => m.fecha).map((m) => m.fecha!.slice(0, 4)));
    s.add(hoy.slice(0, 4));
    return [...s].sort();
  }, [todos, hoy]);

  const cliente = sp.get('cliente') ?? '';
  const anio = sp.get('anio') ?? hoy.slice(0, 4);
  const por = (sp.get('por') ?? 'mes') as Desglose;
  const grupo = sp.get('grupo') ?? '';

  const delCliente = useMemo(() => todos.filter((m) => !cliente || m.cliente_codigo === cliente), [todos, cliente]);
  const delAnio = useMemo(() => delCliente.filter((m) => m.fecha?.startsWith(anio)), [delCliente, anio]);

  const resumen = useMemo(() => resumir(delAnio), [delAnio]);
  const devengadas = delAnio.filter((m) => m.tipo === 'devengada');
  const plazoSolicitada = media(devengadas.map((m) => m.dias_vs_solicitada).filter((d): d is number => d != null));
  const plazoEta = media(devengadas.map((m) => m.dias_vs_eta).filter((d): d is number => d != null));

  // Avisos: lo que el panel no puede fechar o cobrar bien.
  const sinFecha = delCliente.filter((m) => !m.fecha);
  const vencidas = delCliente.filter((m) => m.tipo === 'estimada' && m.fecha && m.fecha < hoy);
  const sinCondicion = delCliente.filter((m) => m.comision_pct == null);

  const filasGrupo = useMemo(() => {
    const map = new Map<string, Movimiento[]>();
    for (const m of delAnio) {
      const k = claveDe(m, por);
      map.set(k, [...(map.get(k) ?? []), m]);
    }
    return [...map.entries()].map(([clave, movs]) => {
      const r = resumir(movs);
      const base = r.devengada_base + r.estimada_base;
      const total = r.devengada_eur + r.estimada_eur;
      const dev = movs.filter((m) => m.tipo === 'devengada').map((m) => m.dias_vs_solicitada).filter((d): d is number => d != null);
      return { clave, ...r, total, pct: base > 0 ? (total / base) * 100 : null, plazo: media(dev) };
    }).sort((a, b) => (por === 'mes' ? a.clave.localeCompare(b.clave) : b.total - a.total));
  }, [delAnio, por]);

  const detalle = useMemo(() => (grupo ? delAnio.filter((m) => claveDe(m, por) === grupo) : delAnio), [delAnio, grupo, por]);

  return (
    <div className="mx-auto max-w-[1400px]">
      <div className="mb-4">
        <h1 className="font-display text-2xl font-extrabold text-slate-900">Comisiones</h1>
        <p className="mt-0.5 max-w-4xl text-sm text-slate-500">
          Comisión PM de los pedidos de cliente confirmados (no cotizaciones). Se <b>devenga en la fecha de entrega</b>: la que se indica al
          marcar el pedido ENTREGADO o, en el histórico, la de cada entrega registrada. Lo pendiente se <b>estima</b> en la ETA o, si no hay,
          en la fecha de entrega solicitada por el cliente. El % sale de las condiciones comerciales de cada cliente.
        </p>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <Grupo label="Cliente">
          <Pildora activo={!cliente} onClick={() => set('cliente', '')}>Todos</Pildora>
          {clientes.map((c) => <Pildora key={c} activo={cliente === c} onClick={() => set('cliente', c)}>{c}</Pildora>)}
        </Grupo>
        <Grupo label="Año">
          {anios.map((a) => <Pildora key={a} activo={anio === a} onClick={() => set('anio', a)}>{a}</Pildora>)}
        </Grupo>
        <Grupo label="Desglose por">
          {DESGLOSES.map((d) => <Pildora key={d.key} activo={por === d.key} onClick={() => set('por', d.key)}>{d.label}</Pildora>)}
        </Grupo>
      </div>

      {q.isLoading && <div className="py-10"><Spinner /></div>}
      {q.error && <p role="alert" className="rounded-md bg-bad-50 px-3 py-2 text-sm text-bad-700">No se pudieron cargar las comisiones. {(q.error as Error).message}</p>}

      {q.data && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Tarjeta label={`Devengada ${anio}`} valor={fmt.eur(resumen.devengada_eur)}
              pie={`sobre ${fmt.eur(resumen.devengada_base)} entregados`} />
            <Tarjeta label={`Estimada ${anio} (pendiente)`} valor={fmt.eur(resumen.estimada_eur)}
              pie={`sobre ${fmt.eur(resumen.estimada_base)} por entregar`} />
            <Tarjeta label={`Previsión total ${anio}`} valor={fmt.eur(resumen.devengada_eur + resumen.estimada_eur)}
              pie={`${fmt.int(resumen.pedidos)} pedidos · % medio ${fmt.pct(
                resumen.devengada_base + resumen.estimada_base > 0
                  ? ((resumen.devengada_eur + resumen.estimada_eur) / (resumen.devengada_base + resumen.estimada_base)) * 100
                  : null,
              )}`} />
            <Tarjeta label="Plazo de entrega medio" alerta={(plazoSolicitada ?? 0) > 7}
              valor={plazoSolicitada == null ? '—' : diasTexto(Math.round(plazoSolicitada))}
              pie={<>frente a la fecha solicitada · vs ETA: {plazoEta == null ? '—' : diasTexto(Math.round(plazoEta))}</>} />
          </div>

          {(vencidas.length > 0 || sinFecha.length > 0 || sinCondicion.length > 0) && (
            <ul className="space-y-1 rounded-md border border-warn-300 bg-warn-50 px-3 py-2 text-xs text-warn-700">
              {vencidas.length > 0 && (
                <li><b>{fmt.int(new Set(vencidas.map((m) => m.pedido_id)).size)} pedido(s) con la fecha estimada ya pasada</b> y sin entregar
                  ({fmt.eur(vencidas.reduce((s, m) => s + m.comision_eur, 0))} de comisión): actualiza la ETA o márcalos entregados.</li>
              )}
              {sinFecha.length > 0 && (
                <li><b>{fmt.int(sinFecha.length)} línea(s) sin fecha</b> ({fmt.eur(sinFecha.reduce((s, m) => s + m.comision_eur, 0))}): no tienen
                  entrega, ETA ni fecha solicitada, así que no caen en ningún año.</li>
              )}
              {sinCondicion.length > 0 && (
                <li><b>{fmt.int(sinCondicion.length)} línea(s) sin condición comercial</b> ({fmt.eur(sinCondicion.reduce((s, m) => s + m.base_eur, 0))} de
                  base): cuentan con 0 % de comisión hasta que su producto tenga marca y condición. <Link className="underline" to="/admin/condiciones-comerciales">Condiciones comerciales</Link></li>
              )}
            </ul>
          )}

          <GraficoMensual movs={delAnio} anio={anio} hoy={hoy} />

          <section className="rounded-lg border border-slate-200 bg-white shadow-sm">
            <header className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-5 py-3">
              <h2 className="font-display text-sm font-bold">Desglose {anio} por {DESGLOSES.find((d) => d.key === por)?.label.toLowerCase()}</h2>
              <span className="text-xs text-slate-500">Pulsa una fila para ver su detalle abajo.</span>
            </header>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-2 text-left">{DESGLOSES.find((d) => d.key === por)?.label}</th>
                    <th className="px-3 py-2 text-right">Pedidos</th>
                    <th className="px-3 py-2 text-right">Base entregada</th>
                    <th className="px-3 py-2 text-right">Devengada</th>
                    <th className="px-3 py-2 text-right">Base pendiente</th>
                    <th className="px-3 py-2 text-right">Estimada</th>
                    <th className="px-3 py-2 text-right">Total</th>
                    <th className="px-3 py-2 text-right">% medio</th>
                    <th className="px-4 py-2 text-right" title="Días de media entre la fecha solicitada y la entrega (+ = tarde)">Plazo vs solicitada</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filasGrupo.length === 0 && (
                    <tr><td colSpan={9} className="px-4 py-6 text-center text-sm text-slate-500">Sin comisiones con fecha en {anio}.</td></tr>
                  )}
                  {filasGrupo.map((f) => (
                    <tr key={f.clave} onClick={() => set('grupo', grupo === f.clave ? '' : f.clave)}
                      className={`cursor-pointer hover:bg-slate-50 ${grupo === f.clave ? 'bg-accent-50' : ''}`}>
                      <td className="px-4 py-2 font-medium text-slate-800">{por === 'mes' ? etiquetaMes(f.clave) : f.clave}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmt.int(f.pedidos)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-500">{fmt.eur(f.devengada_base)}</td>
                      <td className="px-3 py-2 text-right font-semibold tabular-nums text-slate-900">{fmt.eur(f.devengada_eur)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-500">{fmt.eur(f.estimada_base)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-accent-700">{fmt.eur(f.estimada_eur)}</td>
                      <td className="px-3 py-2 text-right font-semibold tabular-nums">{fmt.eur(f.total)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmt.pct(f.pct)}</td>
                      <td className={`px-4 py-2 text-right tabular-nums ${(f.plazo ?? 0) > 0 ? 'text-bad-700' : 'text-slate-600'}`}>{f.plazo == null ? '—' : diasTexto(Math.round(f.plazo))}</td>
                    </tr>
                  ))}
                </tbody>
                {filasGrupo.length > 1 && (
                  <tfoot className="border-t border-slate-200 bg-slate-50 text-sm font-semibold">
                    <tr>
                      <td className="px-4 py-2">Total {anio}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmt.int(resumen.pedidos)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmt.eur(resumen.devengada_base)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmt.eur(resumen.devengada_eur)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmt.eur(resumen.estimada_base)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmt.eur(resumen.estimada_eur)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmt.eur(resumen.devengada_eur + resumen.estimada_eur)}</td>
                      <td className="px-3 py-2" colSpan={2} />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </section>

          <section>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <h2 className="font-display text-sm font-bold">Detalle por línea y entrega</h2>
              {grupo && (
                <button type="button" onClick={() => set('grupo', '')}
                  className="cursor-pointer rounded-full border border-accent-300 bg-accent-50 px-2 py-0.5 text-xs text-accent-700 hover:bg-accent-100">
                  {por === 'mes' ? etiquetaMes(grupo) : grupo} ✕
                </button>
              )}
            </div>
            <DetalleComisiones rows={detalle} hoy={hoy} />
          </section>
        </div>
      )}
    </div>
  );
}

/** Doce meses del año: devengada (sólida) y estimada (rayada) apiladas. */
function GraficoMensual({ movs, anio, hoy }: { movs: Movimiento[]; anio: string; hoy: string }) {
  const meses = Array.from({ length: 12 }, (_, i) => `${anio}-${String(i + 1).padStart(2, '0')}`);
  const serie = meses.map((ym) => {
    const r = resumir(movs.filter((m) => m.fecha?.startsWith(ym)));
    return { ym, dev: r.devengada_eur, est: r.estimada_eur };
  });
  const max = Math.max(1, ...serie.map((s) => s.dev + s.est));
  const W = 900, H = 240, padL = 8, padR = 8, padT = 18, padB = 26;
  const bw = (W - padL - padR) / 12;
  const alto = (v: number) => (v / max) * (H - padT - padB);
  const mesHoy = hoy.slice(0, 7);
  const totDev = serie.reduce((s, x) => s + x.dev, 0);
  const totEst = serie.reduce((s, x) => s + x.est, 0);
  return (
    <section className="rounded-lg border border-slate-200 bg-white shadow-sm">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-5 py-3">
        <h2 className="font-display text-sm font-bold">Comisión {anio} mes a mes</h2>
        <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
          <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-sm bg-accent-600" /> Devengada: {fmt.eur(totDev)}</span>
          <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-sm border border-accent-600 bg-accent-100" /> Estimada: {fmt.eur(totEst)}</span>
        </div>
      </header>
      <div className="px-5 py-4">
        <svg viewBox={`0 0 ${W} ${H}`} className="h-60 w-full" role="img" aria-label={`Comisión devengada y estimada por mes de ${anio}`}>
          <defs>
            <pattern id="com-rayado" width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
              <rect width="5" height="5" fill="#DCE6F8" />
              <line x1="0" y1="0" x2="0" y2="5" stroke="#2456B8" strokeWidth="1.5" />
            </pattern>
          </defs>
          <line x1={padL} y1={H - padB} x2={W - padR} y2={H - padB} stroke="#CBD5E1" strokeWidth="1" />
          {serie.map((s, i) => {
            const x = padL + bw * i + bw * 0.2;
            const w = bw * 0.6;
            const hDev = alto(s.dev);
            const hEst = alto(s.est);
            const base = H - padB;
            return (
              <g key={s.ym}>
                {s.dev > 0 && (
                  <rect x={x} width={w} y={base - hDev} height={hDev} fill="#2456B8">
                    <title>{`${MESES[i]} ${anio} · devengada ${fmt.eur(s.dev)}`}</title>
                  </rect>
                )}
                {s.est > 0 && (
                  <rect x={x} width={w} y={base - hDev - hEst} height={hEst} fill="url(#com-rayado)" stroke="#2456B8" strokeWidth="0.5">
                    <title>{`${MESES[i]} ${anio} · estimada ${fmt.eur(s.est)}`}</title>
                  </rect>
                )}
                {s.dev + s.est > 0 && (
                  <text x={x + w / 2} y={base - hDev - hEst - 4} textAnchor="middle" fontSize="10" fill="#475569">
                    {fmt.eur(Math.round(s.dev + s.est)).replace(',00', '')}
                  </text>
                )}
                <text x={padL + bw * i + bw / 2} y={H - 8} textAnchor="middle" fontSize="11"
                  fill={s.ym === mesHoy ? '#2456B8' : '#475569'} fontWeight={s.ym === mesHoy ? 700 : 400}>{MESES_CORTOS[i]}</text>
              </g>
            );
          })}
        </svg>
        <p className="mt-1 text-xs text-slate-400">
          La parte rayada es estimación (ETA o fecha solicitada). Si una estimación queda en un mes ya pasado es que el pedido va con retraso.
        </p>
      </div>
    </section>
  );
}

function DetalleComisiones({ rows, hoy }: { rows: Movimiento[]; hoy: string }) {
  const columns: Column<Movimiento>[] = useMemo(() => [
    { key: 'fecha', label: 'Fecha', render: (m) => fmt.date(m.fecha), sortValue: (m) => m.fecha ?? '' },
    {
      key: 'tipo', label: 'Tipo',
      render: (m) => m.tipo === 'devengada'
        ? <Badge tone="good">Devengada</Badge>
        : <Badge tone={m.fecha && m.fecha < hoy ? 'warn' : 'accent'}>{m.fecha && m.fecha < hoy ? 'Estimada · vencida' : 'Estimada'}</Badge>,
      sortValue: (m) => m.tipo,
    },
    { key: 'origen', label: 'Fecha según', render: (m) => <span className="text-xs text-slate-500">{ORIGEN_LABEL[m.origen]}</span>, sortValue: (m) => m.origen },
    { key: 'cliente_codigo', label: 'Cliente', sortValue: (m) => m.cliente_codigo },
    {
      key: 'pedido', label: 'Pedido',
      render: (m) => <Link to={pedidoHref(m)} onClick={(e) => e.stopPropagation()} className="font-mono text-xs text-accent-700 hover:underline">{numeroPedido(m)}</Link>,
      sortValue: (m) => numeroPedido(m), searchValue: (m) => `${m.numero_cliente ?? ''} ${m.numero_approx ?? ''}`,
    },
    { key: 'sku', label: 'SKU', render: (m) => <span className="font-mono text-xs">{m.sku ?? '—'}</span>, sortValue: (m) => m.sku ?? '' },
    { key: 'producto_nombre', label: 'Producto', render: (m) => <span className="block max-w-[18rem] truncate text-xs" title={m.producto_nombre ?? undefined}>{m.producto_nombre ?? '—'}</span>, sortValue: (m) => m.producto_nombre ?? '' },
    { key: 'gama_nombre', label: 'Gama', sortValue: (m) => m.gama_nombre ?? '', defaultHidden: true },
    { key: 'categoria_nombre', label: 'Categoría', sortValue: (m) => m.categoria_nombre ?? '' },
    { key: 'subcategoria_nombre', label: 'Subcategoría', sortValue: (m) => m.subcategoria_nombre ?? '' },
    { key: 'unidades', label: 'Uds.', align: 'right', render: (m) => fmt.int(m.unidades), sortValue: (m) => m.unidades, total: 'sum', totalValue: (m) => m.unidades, totalFormat: (n) => fmt.int(n) },
    { key: 'base_eur', label: 'Base', align: 'right', render: (m) => fmt.eur(m.base_eur), sortValue: (m) => m.base_eur, total: 'sum', totalValue: (m) => m.base_eur, totalFormat: (n) => fmt.eur(n) },
    {
      key: 'comision_pct', label: '%', align: 'right',
      render: (m) => m.comision_pct == null ? <span className="text-warn-700" title="Sin condición comercial">—</span> : <span title={m.condicion ?? undefined}>{fmt.pct(m.comision_pct)}</span>,
      sortValue: (m) => m.comision_pct ?? -1,
    },
    { key: 'comision_eur', label: 'Comisión', align: 'right', render: (m) => <span className="font-semibold">{fmt.eur(m.comision_eur)}</span>, sortValue: (m) => m.comision_eur, total: 'sum', totalValue: (m) => m.comision_eur, totalFormat: (n) => fmt.eur(n) },
    { key: 'fecha_solicitada', label: 'Solicitada', render: (m) => fmt.date(m.fecha_solicitada), sortValue: (m) => m.fecha_solicitada ?? '' },
    { key: 'eta', label: 'ETA', render: (m) => fmt.date(m.eta), sortValue: (m) => m.eta ?? '' },
    {
      key: 'dias_vs_solicitada', label: 'Desvío vs solicitada', align: 'right',
      render: (m) => <span className={(m.dias_vs_solicitada ?? 0) > 0 ? 'text-bad-700' : 'text-slate-600'}>{diasTexto(m.dias_vs_solicitada)}</span>,
      sortValue: (m) => m.dias_vs_solicitada ?? -99999,
    },
    {
      key: 'dias_vs_eta', label: 'Desvío vs ETA', align: 'right',
      render: (m) => <span className={(m.dias_vs_eta ?? 0) > 0 ? 'text-bad-700' : 'text-slate-600'}>{diasTexto(m.dias_vs_eta)}</span>,
      sortValue: (m) => m.dias_vs_eta ?? -99999,
    },
  ], [hoy]);

  const filters: DataTableFilter<Movimiento>[] = useMemo(() => [
    { key: 'tipo', label: 'Tipo', options: [{ value: 'devengada', label: 'Devengada' }, { value: 'estimada', label: 'Estimada' }] },
  ], []);

  return (
    <DataTable<Movimiento>
      columns={columns}
      rows={rows}
      rowKey={(m) => `${m.linea_id}-${m.tipo}-${m.fecha ?? ''}-${m.unidades}`}
      defaultSort={{ key: 'fecha', desc: true }}
      filters={filters}
      storageKey="comisiones"
      searchPlaceholder="Buscar por pedido, SKU o producto…"
    />
  );
}

function Grupo({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</span>
      <div className="flex flex-wrap gap-1">{children}</div>
    </div>
  );
}

function Pildora({ activo, onClick, children }: { activo: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={activo}
      className={`cursor-pointer rounded-md border px-2.5 py-1 text-xs font-semibold transition ${activo ? 'border-accent-600 bg-accent-600 text-white' : 'border-slate-300 bg-white text-slate-600 hover:border-accent-300 hover:text-slate-900'}`}>
      {children}
    </button>
  );
}

function Tarjeta({ label, valor, pie, alerta }: { label: string; valor: ReactNode; pie?: ReactNode; alerta?: boolean }) {
  return (
    <div className={`rounded-lg border bg-white px-4 py-3 shadow-sm ${alerta ? 'border-warn-300' : 'border-slate-200'}`}>
      <div className="truncate text-[11px] font-semibold uppercase tracking-wide text-slate-500" title={label}>{label}</div>
      <div className="mt-1 font-display text-2xl font-extrabold tabular-nums text-slate-900">{valor}</div>
      {pie && <div className="mt-1 truncate text-xs text-slate-500">{pie}</div>}
    </div>
  );
}
